import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import {
  type DeepPartial,
  type DotNotationKeys,
  genericSetProperty,
} from "@/features/utils/genericity";
import type IMDFFeature from "@/libs/imdf/feature";
import type { EditorOptions, SliceProps } from "./types";

const initialState: SliceProps = {
  geojson: {
    changes: {},
    editorTool: null,
    lastChange: null,
    multiSelectionCoords: [],
    options: {
      allowMultiSelect: false,
      debounceEdition: false,
      enableSuperHighlight: false,
      hideAllFeatures: false,
      prioritizeTheme: false,
      useZenithalView: false,
    },
    selectedFeatureIds: [],
  },
};

const imdfEditorOptionName = "baseLayersEditor/setIMDFEditorOption";
export function setIMDFEditorOption<T extends DotNotationKeys<EditorOptions>>(
  key: T,
  value: boolean,
): PayloadAction<{ key: T; value: boolean }> {
  return {
    payload: { key, value },
    type: imdfEditorOptionName,
  };
}
setIMDFEditorOption.type = imdfEditorOptionName;

export const BaseLayersEditorSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(setIMDFEditorOption, (state, action) => {
      genericSetProperty(
        state.geojson.options,
        action.payload.key,
        action.payload.value,
      );
    });
  },
  initialState,
  name: "baseLayersEditor",
  reducers: {
    addIMDFFeature: (state, action: PayloadAction<IMDFFeature>) => {
      const feature = action.payload;

      state.geojson.changes[feature.id] = {
        item: feature,
        type: "new",
      };
    },
    deleteIMDFFeatures: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload;

      ids.forEach((id) => {
        state.geojson.changes[id] = {
          type: "deleted",
        };
      });
    },
    resetEditorChanges: (state) => {
      state.geojson.changes = {};
    },
    setEditorTool: (
      state,
      action: PayloadAction<SliceProps["geojson"]["editorTool"]>,
    ) => {
      state.geojson.editorTool = action.payload;
    },
    setLastChange: (
      state,
      action: PayloadAction<SliceProps["geojson"]["lastChange"]>,
    ) => {
      state.geojson.lastChange = action.payload;
    },
    // IMDF
    setMultiSelectionCoords: (
      state,
      action: PayloadAction<SliceProps["geojson"]["multiSelectionCoords"]>,
    ) => {
      state.geojson.multiSelectionCoords = action.payload;
    },
    setSelectedIMDFFeatureIds: (
      state,
      action: PayloadAction<IMDFFeature["id"][]>,
    ) => {
      state.geojson.selectedFeatureIds = action.payload;
    },
    setSelectedIMDFFeatures: (state, action: PayloadAction<IMDFFeature[]>) => {
      state.geojson.selectedFeatureIds = action.payload.map(
        (feature) => feature.id,
      );
    },
    toggleSelectedIMDFFeature: (state, action: PayloadAction<IMDFFeature>) => {
      const feature = action.payload;
      const featureId = feature.id;
      if (state.geojson.selectedFeatureIds.includes(featureId)) {
        state.geojson.selectedFeatureIds =
          state.geojson.selectedFeatureIds.filter(
            (selectedFeatureId) => selectedFeatureId !== featureId,
          );
      } else {
        state.geojson.selectedFeatureIds = [
          ...state.geojson.selectedFeatureIds,
          featureId,
        ];
      }
    },
    toggleSelectedIMDFFeatureIds: (
      state,
      action: PayloadAction<IMDFFeature["id"][]>,
    ) => {
      action.payload.forEach((featureId) => {
        state.geojson.selectedFeatureIds =
          state.geojson.selectedFeatureIds.includes(featureId)
            ? state.geojson.selectedFeatureIds.filter(
                (selectedFeatureId) => selectedFeatureId !== featureId,
              )
            : [...state.geojson.selectedFeatureIds, featureId];
      });
    },
    updateIMDFFeatures: (
      state,
      action: PayloadAction<{
        updates: Array<{ id: string; changes: DeepPartial<IMDFFeature> }>;
      }>,
    ) => {
      const { updates } = action.payload;

      updates.forEach(({ id, changes }) => {
        let item: DeepPartial<IMDFFeature>;

        // Merge with previous changes
        if (state.geojson.changes[id]?.type === "updated") {
          item = deepMergeKeepArrays(
            state.geojson.changes[id]?.item ?? {},
            changes,
          );
        } else {
          item = changes;
        }

        state.geojson.changes[id] = {
          item,
          type: "updated",
        };
      });
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  // GeoJSON
  updateIMDFFeatures,
  deleteIMDFFeatures,
  addIMDFFeature,
  setSelectedIMDFFeatures,
  setSelectedIMDFFeatureIds,
  toggleSelectedIMDFFeatureIds,
  toggleSelectedIMDFFeature,
  resetEditorChanges,
  setLastChange,
  setMultiSelectionCoords,
  setEditorTool,
} = BaseLayersEditorSlice.actions;

export default BaseLayersEditorSlice.reducer;
